// Five-card overview grid for WAF dashboard / events. POST
// /log/smart_parse_overview_strip with site, time_range, and conditions.

import React from "react";
import axios from "../../../../../../utils/axios/v1/userAxios";
import { Box, Stack, Typography, Skeleton } from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import BlockIcon from "@mui/icons-material/BlockOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BoltIcon from "@mui/icons-material/BoltOutlined";
import PublicIcon from "@mui/icons-material/PublicOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import colors from "../../../../../../utils/colors";
import useHome from "../../../../../../hooks/user/useHome";
import { getPeriodString } from "../../../../application/analytics/common";
import WAFOverviewMetricCard, {
  WAFOverviewDeltaPill,
  WAFOverviewIconChip,
  WAFOverviewStatNumber,
  WAF_OVERVIEW_ICON_SX,
  wafOverviewGridSx,
  wafOverviewStatNumberSx,
  wafOverviewSubSx,
} from "./WAFOverviewMetricCard";

const nfmt = (n) => (typeof n === "number" && isFinite(n) ? n.toLocaleString() : "—");

export { wafOverviewGridSx };

function stripPeriodLabel(timeRange, customDateRange) {
  if (!timeRange) return "7d";
  if (timeRange.period) return getPeriodString(timeRange.period, customDateRange);
  if (timeRange.from && timeRange.to) {
    return getPeriodString("custom", [timeRange.from, timeRange.to]);
  }
  return "selected range";
}

export default function WAFEventOverviewStrip({ siteId, timeRange, conditions, customDateRange }) {
  const { curProjectId } = useHome();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const abortRef = React.useRef(null);

  const conditionsKey = React.useMemo(
    () => JSON.stringify(conditions || []),
    [conditions]
  );
  const timeRangeKey = React.useMemo(
    () => JSON.stringify(timeRange || {}),
    [timeRange]
  );

  React.useEffect(() => {
    if (!timeRange) {
      setLoading(false);
      return undefined;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    const body = { time_range: timeRange };
    if (curProjectId) body.project_id = curProjectId;
    if (siteId && siteId !== "all") body.site_id = siteId;
    if (conditions?.length) body.conditions = conditions;
    axios.post("log/smart_parse_overview_strip", body, { signal: ctrl.signal })
      .then((r) => setData(r.data))
      .catch((e) => { if (e.code !== "ERR_CANCELED" && e.message !== "canceled") setData(null); })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [siteId, curProjectId, timeRangeKey, conditionsKey, timeRange, conditions]);

  const d = data || {};
  const periodLabel = stripPeriodLabel(timeRange, customDateRange);
  const deltaUp = typeof d.delta_pct === "number" && d.delta_pct >= 0;
  const deltaCount = typeof d.total_24h === "number" && typeof d.prev_24h === "number"
    ? d.total_24h - d.prev_24h
    : null;

  const skeletonStat = <Skeleton variant="rounded" width={72} height={32} sx={{ borderRadius: "6px" }} />;

  return (
    <Box sx={wafOverviewGridSx}>
      <WAFOverviewMetricCard
        highlight
        icon={<WAFOverviewIconChip variant="primary"><ShieldOutlinedIcon sx={WAF_OVERVIEW_ICON_SX} /></WAFOverviewIconChip>}
        label={`Total detections · ${periodLabel}`}
      >
        {loading ? skeletonStat : (
          <>
            <WAFOverviewStatNumber value={nfmt(d.total_24h)} unit="events" />
            {typeof deltaCount === "number" && d.prev_24h > 0 && (
              <WAFOverviewDeltaPill deltaCount={deltaCount} deltaUp={deltaUp} />
            )}
          </>
        )}
      </WAFOverviewMetricCard>

      <WAFOverviewMetricCard
        icon={<WAFOverviewIconChip variant="primary"><BlockIcon sx={WAF_OVERVIEW_ICON_SX} /></WAFOverviewIconChip>}
        label="Blocked"
      >
        {loading ? skeletonStat : (
          <>
            <WAFOverviewStatNumber value={nfmt(d.blocked)} />
            <Typography sx={wafOverviewSubSx}>
              {typeof d.blocked_pct === "number" && d.total_24h > 0
                ? `${d.blocked_pct}% of total · `
                : ""}
              {typeof d.monitor_passed === "number"
                ? <><Box component="span" sx={{ fontWeight: 600, color: colors.neutral[1000] }}>{nfmt(d.monitor_passed)}</Box> passed in monitor</>
                : "—"}
            </Typography>
          </>
        )}
      </WAFOverviewMetricCard>

      <WAFOverviewMetricCard
        icon={<WAFOverviewIconChip variant="primary"><ErrorOutlineIcon sx={WAF_OVERVIEW_ICON_SX} /></WAFOverviewIconChip>}
        label="Critical"
      >
        {loading ? skeletonStat : (
          <>
            <WAFOverviewStatNumber value={nfmt(d.critical)} />
            <Typography sx={wafOverviewSubSx}>
              {typeof d.critical_unique_ips === "number" && d.critical > 0
                ? <>From <Box component="span" sx={{ fontWeight: 600, color: colors.neutral[1000] }}>{nfmt(d.critical_unique_ips)} unique IPs</Box></>
                : "No critical events"}
            </Typography>
          </>
        )}
      </WAFOverviewMetricCard>

      <WAFOverviewMetricCard
        icon={<WAFOverviewIconChip variant="primary"><BoltIcon sx={WAF_OVERVIEW_ICON_SX} /></WAFOverviewIconChip>}
        label="Top attack"
      >
        {loading ? <Skeleton variant="rounded" width={140} height={24} sx={{ borderRadius: "6px" }} /> : d.top_attack_name ? (
          <>
            <Typography sx={{
              fontSize: 15,
              fontWeight: 600,
              lineHeight: "22px",
              color: colors.neutral[1000],
            }}>
              {d.top_attack_name}
            </Typography>
            <Typography sx={wafOverviewSubSx}>
              <Box component="span" sx={{ fontWeight: 600, color: colors.neutral[1000] }}>
                {nfmt(d.top_attack_count)}
              </Box>
              {" "}event{d.top_attack_count === 1 ? "" : "s"}
              {d.top_attack_method && d.top_attack_path
                ? <> · <Box component="span" sx={ { fontSize: 11, color: colors.neutral[600] }}>{d.top_attack_method} {d.top_attack_path}</Box></>
                : null}
            </Typography>
          </>
        ) : (
          <Typography component="span" data-overview-metric sx={{ ...wafOverviewStatNumberSx, color: colors.neutral[400], fontSize: 24 }}>—</Typography>
        )}
      </WAFOverviewMetricCard>

      <WAFOverviewMetricCard
        icon={<WAFOverviewIconChip variant="primary"><PublicIcon sx={WAF_OVERVIEW_ICON_SX} /></WAFOverviewIconChip>}
        label="Sources"
        sx={{ gridColumn: { xs: "auto", sm: "auto", md: "1 / -1", lg: "auto" } }}
      >
        {loading ? skeletonStat : (
          <>
            <WAFOverviewStatNumber value={nfmt(d.source_countries)} unit="countries" />
            {d.spike_country?.country ? (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{
                mt: 1,
                display: "inline-flex",
                alignSelf: "flex-start",
                px: 1,
                py: 0.375,
                borderRadius: "6px",
                backgroundColor: colors.yellow[10],
                color: colors.yellow[200],
                border: `1px solid rgba(223, 180, 0, 0.35)`,
                fontSize: 11,
                fontWeight: 600,
              }}>
                <WarningAmberIcon sx={{ fontSize: 12 }} />
                {d.spike_country.country} spike
              </Stack>
            ) : (
              <Typography sx={{ ...wafOverviewSubSx, color: colors.neutral[500], fontStyle: "italic" }}>balanced</Typography>
            )}
          </>
        )}
      </WAFOverviewMetricCard>
    </Box>
  );
}
